import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './RazorpayPayment.css';

interface RazorpayPaymentProps {
    amount: number;
    project: string;
    donationFrequency: string;
    onSuccess: (paymentData: any) => void;
    onFailure: (error: any) => void;
}

declare global {
    interface Window {
        Razorpay: any;
    }
}

const RazorpayPayment: React.FC<RazorpayPaymentProps> = ({
    amount,
    project,
    donationFrequency,
    onSuccess,
    onFailure
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [razorpayLoaded, setRazorpayLoaded] = useState(false);

    // Load Razorpay SDK
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => setRazorpayLoaded(true);
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const handlePayment = async () => {
        if (!razorpayLoaded) {
            onFailure({ message: t('donationForm.razorpaySdkNotLoaded') });
            return;
        }

        setLoading(true);

        try {
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: amount * 100, // Convert to paise
                currency: 'INR',
                name: 'Manjushree Foundation',
                description: `Donation for ${project}`,
                image: '/images/logo.png',
                handler: function (response: any) {
                    onSuccess({
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature,
                        amount,
                        project,
                        donationFrequency,
                        paymentMethod: 'razorpay',
                        paymentStatus: 'success'
                    });
                    setLoading(false);
                },
                prefill: {
                    name: 'Anonymous Donor',
                    email: '',
                    contact: ''
                },
                notes: {
                    project,
                    frequency: donationFrequency,
                    donationType: 'anonymous'
                },
                theme: {
                    color: '#f4c430'
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                        onFailure({ message: t('donationForm.paymentCancelled') });
                    }
                }
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();
        } catch (error) {
            setLoading(false);
            onFailure(error);
        }
    };

    return (
        <div className="razorpay-payment">
            <div className="payment-info">
                <h3 className="payment-title">{t('donationForm.razorpay')}</h3>
                <p className="payment-description">
                    {t('donationForm.razorpayDescription')}
                </p>
                <div className="payment-details">
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.amount')}:</span>
                        <span className="detail-value">₹{amount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.projectLabel')}:</span>
                        <span className="detail-value">{project}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.frequencyLabel')}:</span>
                        <span className="detail-value">{donationFrequency}</span>
                    </div>
                </div>
            </div>

            <button
                type="button"
                className="razorpay-button"
                onClick={handlePayment}
                disabled={loading || !razorpayLoaded}
            >
                {loading ? (
                    <>
                        <span className="spinner"></span>
                        {t('donationForm.processing')}
                    </>
                ) : !razorpayLoaded ? (
                    t('donationForm.loadingRazorpay')
                ) : (
                    <>
                        {t('donationForm.payWithRazorpay', { amount: amount.toLocaleString('en-IN') })}
                    </>
                )}
            </button>

            <div className="secure-payment-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L4 6V12C4 16.4 7.6 20.4 12 22C16.4 20.4 20 16.4 20 12V6L12 2Z" fill="#4caf50" />
                    <path d="M10 14L7 11L8.4 9.6L10 11.2L15.6 5.6L17 7L10 14Z" fill="white" />
                </svg>
                <span>{t('donationForm.securePayment')}</span>
            </div>
        </div>
    );
};

export default RazorpayPayment;
